import { useState } from 'react';
import { api, ApiError } from '../api/client';

/**
 * In-app activation for a client admin (UIX). Drives the browser equivalent of client-activate.mts against
 * the deployment's own /api/v1/activation endpoints. Works while the deployment is Locked (the endpoints are
 * entitlement-exempt); on success the backend hot-reloads the gate, so `onActivated` can refetch status and
 * the app transitions to Licensed with no restart. Both paste and file upload are supported.
 */
export function ActivateScreen({ onActivated }: { onActivated?: () => void }) {
  const [step, setStep] = useState<'bundle' | 'response' | 'done' | 'customer_mismatch'>('bundle');
  const [bundleText, setBundleText] = useState('');
  const [request, setRequest] = useState('');
  const [responseText, setResponseText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [crlText, setCrlText] = useState('');
  const [crlMsg, setCrlMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const msg = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
  const readFile = (f: File | undefined, set: (v: string) => void) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => set(String(r.result ?? ''));
    r.readAsText(f);
  };
  const download = (text: string, filename: string) => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const copyRequest = async () => {
    try { await navigator.clipboard?.writeText(request); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard blocked — the Download button is the fallback */ }
  };

  async function installBundle() {
    setError('');
    let bundle: unknown;
    try { bundle = JSON.parse(bundleText); } catch { setError('That is not valid bundle JSON — paste the bundle from the License Server, or upload bundle.json.'); return; }
    setBusy(true);
    try {
      const res = await api.post<{
        ok: boolean;
        request: string;
        onlineActivation?: { attempted: boolean; ok: boolean; installUuid?: string; reason?: string; message?: string; errorCode?: string };
        diagnostic?: { kind: string };
      }>('/activation/install-bundle', { bundle });
      // 3.3 (the honesty piece): the online result is DISPLAYED, never swallowed. The field
      // failure this fixes: install ok + online refused ("not registered with the control
      // plane") looked like success while the deployment silently ran on air-gap fallback.
      const oa = res.onlineActivation;
      if (oa?.ok) {
        setStep('done');
        onActivated?.();
        return;
      }
      // Customer-mismatch is a HARD stop: the deployment is bound to a different customer. We must NOT
      // offer the air-gap fallback here — that fallback is exactly the escape hatch that let a
      // mismatched license be forced through (BUILD-M9-14). Show the error + a clean retry; the
      // operator must release the binding in the CP Activations view first.
      if (oa?.attempted && !oa.ok && oa.errorCode === 'install_bound_other_customer') {
        setError(
          'This deployment is bound to a different customer. A deployment runs one customer\'s license ' +
          'at a time. To switch customers, an operator must first release the existing binding in the ' +
          'control-plane Activations view, then activate again. Manual (air-gap) activation is not ' +
          'available for a customer switch.',
        );
        setStep('customer_mismatch');
        return;
      }
      if (oa?.attempted && !oa.ok) {
        setError(
          `Bundle installed, but ONLINE activation was refused: ${oa.reason ?? 'unknown reason'}. ` +
          'The deployment can run on local verification for now, but it will not heartbeat and cannot ' +
          'receive revocations until this is fixed — usually the license is not registered on the ' +
          'control plane yet (an operator can register it from the CP console), or the instance is ' +
          'bound elsewhere. You can also continue below with manual (air-gap) activation.',
        );
      }
      setRequest(res.request);
      setStep('response');
    } catch (e) { setError(msg(e)); } finally { setBusy(false); }
  }

  async function installResponse() {
    setError('');
    setBusy(true);
    try {
      const res = await api.post<{ ok: boolean; diagnostic: { kind: string } }>('/activation/install-response', { response: responseText.trim() });
      if (res.diagnostic?.kind === 'ok') { setStep('done'); onActivated?.(); }
      else setError(`Response installed, but the deployment is still not licensed (${res.diagnostic?.kind ?? 'unknown'}). Check that the bundle and response are from the same license.`);
    } catch (e) { setError(msg(e)); } finally { setBusy(false); }
  }

  async function installCrl() {
    setCrlMsg('');
    setBusy(true);
    try {
      const res = await api.post<{ ok: boolean; diagnostic: { kind: string; state?: string } }>('/activation/install-crl', { crl: crlText.trim() });
      const k = res.diagnostic?.kind;
      setCrlMsg(k === 'revoked' ? 'Revocation list applied — this deployment’s license is revoked.' : `Revocation list applied (deployment state: ${k}).`);
      setCrlText('');   // clear the textarea so stale CRL content does not persist after a successful apply
      onActivated?.();
    } catch (e) { setCrlMsg(msg(e)); } finally { setBusy(false); }
  }

  const box = 'w-full text-xs font-mono border border-ink-200 rounded-sm p-2 bg-white';
  const btn = 'text-sm px-4 py-2 rounded-sm font-medium bg-accent text-white disabled:opacity-50';
  const ghost = 'text-sm px-3 py-2 rounded-sm border border-ink-200 bg-white hover:bg-ink-50';

  return (
    <div className="max-w-xl w-full mx-auto border border-ink-200 rounded-sm p-6 bg-white">
      <h2 className="text-lg font-semibold text-ink-900">Activate this deployment</h2>
      <p className="mt-1 text-sm text-ink-600">
        Install the license bundle your provider issued, hand the generated request back for signing, then
        install the signed response. No files leave this machine except the text you copy.
      </p>

      {error && <p className="mt-3 text-sm text-red-700" data-testid="activate-error">{error}</p>}

      {step === 'customer_mismatch' && (
        <div className="mt-4" data-testid="activate-customer-mismatch">
          <p className="text-sm text-ink-600">
            Once an operator has released the existing binding in the control-plane Activations view,
            start a fresh activation attempt.
          </p>
          <div className="mt-3">
            <button type="button" className={btn} onClick={() => window.location.reload()}>
              Activate New Deployment
            </button>
          </div>
        </div>
      )}

      {step === 'bundle' && (
        <div className="mt-4">
          <label className="block text-xs text-ink-500 mb-1">1 · License bundle (paste JSON, or upload bundle.json)</label>
          <textarea rows={5} spellCheck={false} className={box} value={bundleText} onChange={(e) => setBundleText(e.target.value)} placeholder="paste bundle.json contents…" />
          <div className="mt-2 flex items-center gap-3">
            <input type="file" accept="application/json,.json" onChange={(e) => readFile(e.target.files?.[0], setBundleText)} className="text-sm" />
            <button type="button" className={btn} disabled={busy || !bundleText.trim()} onClick={installBundle}>
              {busy ? 'Installing…' : 'Install bundle'}
            </button>
          </div>
        </div>
      )}

      {step === 'response' && (
        <div className="mt-4">
          <label className="block text-xs text-ink-500 mb-1">2 · Activation request — send this to your provider to sign</label>
          <textarea rows={4} readOnly spellCheck={false} className={box} value={request} data-testid="activate-request" />
          <div className="mt-2 flex items-center gap-3">
            <button type="button" className={ghost} onClick={copyRequest}>{copied ? 'Copied ✓' : 'Copy request'}</button>
            <button type="button" className={ghost} onClick={() => download(request, 'activation-request.txt')}>Download request</button>
          </div>
          <label className="block text-xs text-ink-500 mb-1 mt-4">3 · Signed response (paste, or upload the file)</label>
          <textarea rows={4} spellCheck={false} className={box} value={responseText} onChange={(e) => setResponseText(e.target.value)} placeholder="paste the signed response…" />
          <div className="mt-2 flex items-center gap-3">
            <input type="file" accept=".txt,text/plain" onChange={(e) => readFile(e.target.files?.[0], setResponseText)} className="text-sm" />
            <button type="button" className={btn} disabled={busy || !responseText.trim()} onClick={installResponse}>
              {busy ? 'Activating…' : 'Activate'}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="mt-4 border border-green-300 bg-green-50 rounded-sm p-4" data-testid="activate-done">
          <p className="text-sm font-medium text-green-800">Activated. This deployment is now licensed.</p>
          <p className="mt-1 text-sm text-ink-700">The license is enforcing — no restart needed.</p>
        </div>
      )}

      <details className="mt-6 border-t border-ink-200 pt-4">
        <summary className="text-sm text-ink-600 cursor-pointer">Apply a revocation list (CRL)</summary>
        <p className="mt-2 text-xs text-ink-500">
          Only needed if your provider has <em>revoked</em> a license (e.g. contract ended or a copy was
          detected). Your provider publishes a signed revocation list (CRL); paste or upload it here to apply
          it. It is verified against the pinned root, so a forged list is rejected. If this deployment's
          license is on the list, the app locks — but your data is preserved and recoverable. You do not need
          this for normal activation.
        </p>
        <textarea rows={3} spellCheck={false} className={box + ' mt-2'} value={crlText} onChange={(e) => setCrlText(e.target.value)} placeholder="paste the CRL (base64)…" />
        <div className="mt-2 flex items-center gap-3">
          <input type="file" accept=".txt,text/plain" onChange={(e) => readFile(e.target.files?.[0], setCrlText)} className="text-sm" />
          <button type="button" className={ghost} disabled={busy || !crlText.trim()} onClick={installCrl}>Apply CRL</button>
        </div>
        {crlMsg && <p className="mt-2 text-sm text-ink-700" data-testid="crl-msg">{crlMsg}</p>}
      </details>
    </div>
  );
}
